import React, { useState, useEffect, useMemo } from "react";
import { PERMISSIONS } from "../../constants/permissions";

// derive grouping
const GROUP_LABELS = {
  USER: "USER MANAGEMENT",
  ROLE: "ROLE MANAGEMENT",
  PROJECT: "PROJECTS",
  TASK: "TASKS",
  CATEGORY: "JOB CATEGORIES",
  WORKFLOW: "WORKFLOW BUILDER",
  WORKFLOW_RUN: "WORKFLOW RUNNER",
};

function groupPermissions(permsMap) {
  const groups = {};
  Object.entries(permsMap).forEach(([key, value]) => {
    const prefix = key.split("_")[0];
    const label = GROUP_LABELS[prefix] || prefix;
    if (!groups[label]) groups[label] = [];
    groups[label].push({ key, value });
  });
  return groups;
}

const PERMISSION_GROUPS = groupPermissions(PERMISSIONS);

// Keeps only the section entries that exist in `permsMap`, and adds an "Other"
// section for anything in the map no section lists — a permission added
// without updating the sections still shows up and can still be granted.
function resolveSections(sections, permsMap) {
  const available = new Set(Object.values(permsMap));
  const listed = new Set();
  const resolved = sections
    .map((section) => ({
      ...section,
      permissions: section.permissions.filter((p) => {
        if (!p.value || !available.has(p.value) || listed.has(p.value)) return false;
        listed.add(p.value);
        return true;
      }),
    }))
    .filter((section) => section.permissions.length > 0);

  const others = Object.values(permsMap)
    .filter((value) => !listed.has(value))
    .map((value) => ({ value, label: value }));
  if (others.length > 0) {
    resolved.push({ title: "Other", description: "Permissions without a section yet.", permissions: others });
  }
  return resolved;
}

function matches(perm, section, q) {
  if (!q) return true;
  return [perm.label, perm.hint, perm.value, section.title]
    .filter(Boolean)
    .some((text) => text.toLowerCase().includes(q));
}

function SectionedPermissions({ sections, granted, editable, query, onQueryChange, onToggle, onSetMany }) {
  const q = query.trim().toLowerCase();
  const total = sections.reduce((n, s) => n + s.permissions.length, 0);
  const grantedCount = sections.reduce(
    (n, s) => n + s.permissions.filter((p) => granted.includes(p.value)).length,
    0,
  );
  const visible = sections
    .map((s) => ({ ...s, shown: s.permissions.filter((p) => matches(p, s, q)) }))
    .filter((s) => s.shown.length > 0);

  return (
    <div className="rm-sections">
      <div className="rm-sections-toolbar">
        <input
          type="search"
          className="rm-sections-search"
          placeholder="Search permissions"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          aria-label="Search permissions"
        />
        <span className="rm-sections-total">
          {grantedCount} of {total} granted
        </span>
      </div>

      {visible.length === 0 && <div className="rm-empty">No permissions match “{query}”.</div>}

      {visible.map((section) => {
        const values = section.permissions.map((p) => p.value);
        const on = values.filter((v) => granted.includes(v)).length;
        return (
          <section key={section.title} className="rm-section">
            <header className="rm-section-head">
              <div>
                <h3>{section.title}</h3>
                {section.description && <p>{section.description}</p>}
              </div>
              <div className="rm-section-meta">
                <span className={`rm-section-count ${on === values.length ? "all" : on > 0 ? "some" : ""}`.trim()}>
                  {on}/{values.length}
                </span>
                {editable && (
                  <>
                    <button type="button" className="rm-link-btn" onClick={() => onSetMany(values, true)} disabled={on === values.length}>
                      Select all
                    </button>
                    <button type="button" className="rm-link-btn" onClick={() => onSetMany(values, false)} disabled={on === 0}>
                      Clear
                    </button>
                  </>
                )}
              </div>
            </header>
            <ul className="rm-section-list">
              {section.shown.map((perm) => {
                const checked = granted.includes(perm.value);
                return (
                  <li key={perm.value} className={checked ? "checked" : ""}>
                    <label>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!editable}
                        onChange={() => onToggle(perm.value)}
                      />
                      <span className="rm-perm-text">
                        <span className="rm-perm-label">{perm.label}</span>
                        {perm.hint && <span className="rm-perm-hint">{perm.hint}</span>}
                        <code className="rm-perm-key">{perm.value}</code>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

export default function RolePermissionsPanel({
  role,
  editable = false,
  onChange,
  onNameChange,
  onDescriptionChange,
  onSave,
  onCancel,
  onEdit,
  onDelete,
  permissionsMap,
  sections,
}) {
  const permissionGroups = permissionsMap ? groupPermissions(permissionsMap) : PERMISSION_GROUPS;
  const resolvedSections = useMemo(
    () => (sections ? resolveSections(sections, permissionsMap || PERMISSIONS) : null),
    [sections, permissionsMap],
  );
  const [query, setQuery] = useState("");
  const [localPermissions, setLocalPermissions] = useState(
    role?.permissions || [],
  );
  const isSystem = role?.isSystem;

  useEffect(() => {
    setLocalPermissions(role?.permissions || []);
  }, [role]);

  // Every permission in `values` on, or every one off, in one step
  const setMany = (values, on) => {
    if (!editable) return;
    setLocalPermissions((prev) => {
      const next = on
        ? [...new Set([...prev, ...values])]
        : prev.filter((p) => !values.includes(p));
      onChange && onChange(next);
      return next;
    });
  };

  const toggle = (perm) => {
    if (!editable) return;
    setLocalPermissions((prev) => {
      const next = prev.includes(perm)
        ? prev.filter((p) => p !== perm)
        : [...prev, perm];
      onChange && onChange(next);
      return next;
    });
  };

  if (!role) {
    return (
      <div className="rm-role-permissions-panel">
        Select a role to view details
      </div>
    );
  }

  return (
    <div className="rm-role-permissions-panel">
      <div className="rm-panel-header">
        {editable ? (
          <div className="rm-role-header-fields">
            <input
              type="text"
              value={role.name || ""}
              onChange={(e) => onNameChange && onNameChange(e.target.value)}
              placeholder="Role name"
            />
            <input
              type="text"
              value={role.description || ""}
              onChange={(e) =>
                onDescriptionChange && onDescriptionChange(e.target.value)
              }
              placeholder="Description (optional)"
            />
          </div>
        ) : (
          <h2>Permissions for: {role.name || "-"}</h2>
        )}
        {editable ? (
          <div className="rm-action-row">
            <button className="rm-btn-primary" onClick={onSave}>
              Save Changes
            </button>
            <button className="rm-btn-secondary" onClick={onCancel}>
              Cancel
            </button>
          </div>
        ) : (
          !isSystem && (
            <button className="rm-btn-secondary" onClick={onEdit}>
              Edit
            </button>
          )
        )}
      </div>
      {resolvedSections ? (
        <SectionedPermissions
          sections={resolvedSections}
          granted={localPermissions}
          editable={editable}
          query={query}
          onQueryChange={setQuery}
          onToggle={toggle}
          onSetMany={setMany}
        />
      ) : (
      <div className="rm-permissions-grid">
        {Object.entries(permissionGroups).map(([group, perms]) => (
          <div key={group} className="rm-perm-group">
            <strong>{group}</strong>
            <ul>
              {perms.map(({ key, value }) => (
                <li
                  key={key}
                  className={localPermissions.includes(value) ? "checked" : ""}
                >
                  <label>
                    <input
                      type="checkbox"
                      checked={localPermissions.includes(value)}
                      disabled={!editable}
                      onChange={() => toggle(value)}
                    />
                    {value.replace(/_/g, " ")}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      )}
      {!isSystem && !editable && (
        <button className="rm-delete-role-btn" onClick={onDelete}>
          Delete Role
        </button>
      )}
    </div>
  );
}
